import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button, Checkbox, Empty, Input, message, Modal, Spin } from "antd";
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { ProjectContext } from "context/ProjectProvider";
import { useCIQMutation, useCIQQuery } from "hooks/ciq-gql-hooks";
import { MUTATION_APPLY_LINKING_CHANGES } from "services/graphQL/mutations";
import { DependencyModel } from "pages/design-package-details-page/components/view-dependencies/model";
import { BID_PACKAGE_LIST_QUERY_MIN } from "pages/planning-workspace/queries/planning-workspace-queries";
import FeatureItem from "components/feature-linking-dialog/feature-item";

interface ManageBidPackagesModalProps {
  open: boolean;
  onClose: () => void;
  designPackageId: string;
  designPackageLabel: string;
  model: DependencyModel | null;
  onChanged: () => void;
  /** Opens on this bid package: its row and linked card are scrolled into view
   *  and the linked card is outlined. */
  focusBidPackageId?: string | null;
}

type BidPackageRow = {
  id: string;
  number: string | null;
  title: string | null;
};

type DraftLink = {
  id: string;
  number: string;
  title: string;
  milestoneId: string;
};

type MilestoneOption = { id: string; name: string };

function draftWithMilestone(
  draft: DraftLink[],
  linkId: string,
  milestoneId: string
): DraftLink[] {
  return draft.map((item) =>
    item.id === linkId ? { ...item, milestoneId } : item
  );
}

function draftWithoutLink(draft: DraftLink[], linkId: string): DraftLink[] {
  return draft.filter((item) => item.id !== linkId);
}

function linksFromModel(
  model: DependencyModel | null,
  fallbackMilestoneId: string | null
): DraftLink[] {
  return (model?.bidPackages ?? []).map((bp) => ({
    id: bp.id,
    number: bp.number ?? "",
    title: bp.name ?? "",
    milestoneId: bp.linkedDpMilestone?.id ?? fallbackMilestoneId ?? ""
  }));
}

function ActionCell(params: {
  data: BidPackageRow;
  context: {
    linkedIds: Set<string>;
    toggleLink: (row: BidPackageRow) => void;
    saving: boolean;
  };
}) {
  const linked = params.context.linkedIds.has(params.data.id);
  const { saving } = params.context;
  return (
    <button
      type="button"
      disabled={saving}
      className={`flex items-center gap-2 border-none bg-transparent p-0 ${
        saving ? "cursor-not-allowed" : ""
      }`}
      data-testid={`link-action-${params.data.id}`}
      onClick={() => params.context.toggleLink(params.data)}
    >
      <Checkbox checked={linked} disabled={saving} />
      <span
        className={
          linked
            ? "text-sm text-ciq-primary-900 underline"
            : "text-sm text-neutral-800"
        }
      >
        {linked ? "Linked" : "Link"}
      </span>
    </button>
  );
}

export default function ManageBidPackagesModal(
  props: ManageBidPackagesModalProps
) {
  const {
    open,
    onClose,
    designPackageId,
    designPackageLabel,
    model,
    onChanged,
    focusBidPackageId = null
  } = props;

  const { gqlClientForProject } = useContext(ProjectContext);
  const [applyLinking] = useCIQMutation(MUTATION_APPLY_LINKING_CHANGES, {
    client: gqlClientForProject
  });

  const { data: bidPackageListData, loading: listLoading } = useCIQQuery<{
    feature_instance: BidPackageRow[];
  }>(BID_PACKAGE_LIST_QUERY_MIN, {
    client: gqlClientForProject,
    skip: !open || !gqlClientForProject
  });

  const milestones: MilestoneOption[] = (
    model?.designPackage.milestones ?? []
  ).filter((m) => Boolean(m.name));
  const lastMilestoneId = milestones.length
    ? milestones[milestones.length - 1].id
    : null;

  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState<DraftLink[]>(() =>
    linksFromModel(model, lastMilestoneId)
  );
  const [saving, setSaving] = useState(false);
  const gridRef = useRef<AgGridReact<BidPackageRow>>(null);
  const focusCardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open || !focusBidPackageId) return undefined;
    const frame = window.requestAnimationFrame(() =>
      focusCardRef.current?.scrollIntoView?.({ block: "nearest" })
    );
    return () => window.cancelAnimationFrame(frame);
  }, [open, focusBidPackageId]);

  const showFocusedRow = () => {
    if (!focusBidPackageId) return;
    const api = gridRef.current?.api;
    const node = api?.getRowNode(focusBidPackageId);
    if (api && node) api.ensureNodeVisible(node, "middle");
  };

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setDraft(linksFromModel(model, lastMilestoneId));
    // Seed the draft when the dialog opens. Later model refreshes must not
    // wipe in-progress link edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const linkedIds = useMemo(
    () => new Set(draft.map((link) => link.id)),
    [draft]
  );

  const rows = useMemo(() => {
    const all = bidPackageListData?.feature_instance ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((bp) =>
      `${bp.number ?? ""} ${bp.title ?? ""}`.toLowerCase().includes(q)
    );
  }, [bidPackageListData, query]);

  const toggleLink = (row: BidPackageRow) => {
    if (saving) return;
    setDraft((prev) => {
      if (prev.some((link) => link.id === row.id)) {
        return prev.filter((link) => link.id !== row.id);
      }
      if (!lastMilestoneId) {
        message.error("Add a milestone to this design package first");
        return prev;
      }
      return [
        {
          id: row.id,
          number: row.number ?? "",
          title: row.title ?? "",
          milestoneId: lastMilestoneId
        },
        ...prev
      ];
    });
  };

  const columnDefs = useMemo<ColDef<BidPackageRow>[]>(
    () => [
      {
        headerName: "Number",
        field: "number",
        width: 140,
        sortable: true,
        sort: "asc"
      },
      {
        headerName: "Title",
        field: "title",
        flex: 1,
        sortable: true
      },
      {
        headerName: "Action",
        colId: "action",
        width: 140,
        sortable: false,
        cellClass: "ciq-cell-v-center",
        cellRenderer: ActionCell
      }
    ],
    []
  );

  useEffect(() => {
    gridRef.current?.api?.refreshCells({ columns: ["action"], force: true });
  }, [linkedIds, saving]);

  const save = async () => {
    const original = new Map(
      (model?.bidPackages ?? []).map((bp) => [
        bp.id,
        bp.linkedDpMilestone?.id ?? lastMilestoneId ?? ""
      ])
    );
    const inserts = draft.flatMap((link) => {
      const previous = original.get(link.id);
      original.delete(link.id);
      if (previous === link.milestoneId) return [];
      return [
        {
          source_instance_id: designPackageId,
          source_instance_milestone_id: link.milestoneId,
          target_instance_id: link.id,
          link_type: link.milestoneId === lastMilestoneId ? 0 : 4,
          coupling_type: 2
        }
      ];
    });
    const deletes = [...original.keys()].map((id) => ({
      source_instance_id: designPackageId,
      target_instance_id: id
    }));

    if (inserts.length === 0 && deletes.length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    const res = await applyLinking(
      {
        variables: {
          input: { inserts, deletes, updates: [] },
          metadata: { request_source: "PLANNER" }
        }
      },
      { showError: true }
    );
    setSaving(false);
    if (res.success) {
      message.success("Bid Package links updated");
      onChanged();
      onClose();
    } else {
      message.error(res.errorMsg || "Couldn't update Bid Package links");
    }
  };

  return (
    <Modal
      open={open}
      onCancel={() => {
        if (!saving) onClose();
      }}
      closable={!saving}
      mask={{ closable: !saving }}
      keyboard={!saving}
      destroyOnHidden
      width={910}
      styles={{
        body: { marginInline: -24 }
      }}
      title={
        <span className="text-base font-normal text-black text-opacity-85">
          Link Bid Package to{" "}
          <span className="font-semibold">{designPackageLabel}</span>
        </span>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            data-testid="manage-bp-cancel"
            disabled={saving}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            data-testid="manage-bp-save"
            loading={saving}
            disabled={saving}
            onClick={save}
          >
            Save
          </Button>
        </div>
      }
      closeIcon={<CloseOutlined className="text-black text-opacity-45" />}
    >
      <div className="flex min-h-[460px] border-0 border-solid border-y border-ciq-gray-100">
        <div className="flex min-w-0 flex-1 flex-col gap-3 px-3 py-2">
          <Input
            allowClear
            disabled={saving}
            data-testid="assign-bp-search"
            placeholder="Search Bid Packages"
            value={query}
            suffix={<SearchOutlined />}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="ag-theme-alpine relative h-[400px] w-full">
            {listLoading ? (
              <div className="absolute inset-0 z-10 flex items-center justify-center">
                <Spin />
              </div>
            ) : null}
            <AgGridReact<BidPackageRow>
              ref={gridRef}
              rowData={rows}
              columnDefs={columnDefs}
              getRowId={(params) => params.data.id}
              headerHeight={48}
              rowHeight={40}
              suppressMovableColumns
              suppressCellFocus
              onFirstDataRendered={showFocusedRow}
              context={{ linkedIds, toggleLink, saving }}
            />
          </div>
        </div>
        <div className="flex w-96 h-[462px] shrink-0 flex-col gap-4 overflow-auto border-0 border-l border-solid border-ciq-gray-300 bg-ciq-neutral-50 p-4">
          <div>
            <div className="text-sm font-medium capitalize text-black text-opacity-85">
              Linked Design packages Milestone
            </div>
            <div className="mt-2 text-xs text-black text-opacity-45">
              Select the Design Package milestone you wish to link Bid Packages
              with.
            </div>
          </div>
          {draft.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No Bid Packages linked"
            />
          ) : (
            <div className="flex flex-col gap-3 ">
              {draft.map((link) => (
                <div
                  key={link.id}
                  data-testid={`linked-card-${link.id}`}
                  data-focused={link.id === focusBidPackageId || undefined}
                  ref={link.id === focusBidPackageId ? focusCardRef : undefined}
                  className={
                    link.id === focusBidPackageId
                      ? "rounded-md outline outline-2 outline-offset-2 outline-ciq-planner-accent"
                      : undefined
                  }
                >
                  <FeatureItem
                    number={link.number}
                    title={link.title}
                    combineNumberAndTitle
                    showMilestoneSelect
                    milestoneSelectLabel="Linked To"
                    milestones={milestones}
                    selectedFeatureMilestone={milestones.find(
                      (milestone) => milestone.id === link.milestoneId
                    )}
                    disableMilestoneSelect={saving}
                    disableUnlink={saving}
                    onMilestoneChange={(milestone) => {
                      if (saving) return;
                      setDraft((prev) =>
                        draftWithMilestone(prev, link.id, milestone.id)
                      );
                    }}
                    onUnlink={() => {
                      if (saving) return;
                      setDraft((prev) => draftWithoutLink(prev, link.id));
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
